import React, { useState } from 'react';
import { Box, Button, Collapse, TextField, Typography } from '@mui/material';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { isMaskedSecret, issuerLooksLikeEndpoint, suggestJwksUri } from './mcpUtils';

/**
 * The `oauth_config` block, shared by the create dialog and the settings tab so
 * the copy and the validation can't drift apart.
 *
 * Deliberately style-agnostic — it leans on MUI's own `helperText` rather than
 * either host's SCSS, because the two screens style their hints differently.
 *
 * The customer filling this in is copying values out of their identity
 * provider's dashboard, so every label names the thing they are looking for
 * there. The three advanced fields are collapsed because they only apply to
 * providers that issue opaque tokens, which is rare.
 */
const McpOAuthFields = ({ value, errors = {}, onChange, disabled = false }) => {
  const [showAdvanced, setShowAdvanced] = useState(
    !!(value.introspection_endpoint || value.client_id || value.client_secret)
  );

  /**
   * The keys URL is the issuer plus a well-known path in nearly every case, so
   * fill it in as the issuer is typed — but only from a bare host, and never
   * over a URL the customer chose themselves. `suggestJwksUri` returns empty
   * for an issuer with a path, and that leaves the field as it is rather than
   * clearing a value that is probably still right.
   */
  const handleIssuerChange = (next) => {
    const current = (value.jwks_uri || '').trim();
    const suggestion = suggestJwksUri(next);
    onChange('issuer', next);
    if (suggestion && (!current || current === suggestJwksUri(value.issuer))) {
      onChange('jwks_uri', suggestion);
    }
  };

  const secretIsStored = isMaskedSecret(value.client_secret);
  // The reported failure: an authorize endpoint pasted in as the issuer.
  const issuerIsEndpoint = issuerLooksLikeEndpoint(value.issuer);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label="Issuer URL"
        value={value.issuer}
        onChange={(e) => handleIssuerChange(e.target.value)}
        error={!!errors.issuer}
        helperText={
          errors.issuer ||
          (issuerIsEndpoint
            ? 'That looks like an authorize or token endpoint. Use only the base address — the part before /oauth, /authorize or /login.'
            : 'The base address of your login system — not a login or authorize page. Example: https://your-tenant.auth0.com/')
        }
        placeholder="https://your-tenant.auth0.com/"
        size="small"
        fullWidth
        required
        disabled={disabled}
        // Tinted, not an error: a path can be legitimate, so this warns without
        // blocking a save that may well be correct.
        slotProps={
          issuerIsEndpoint && !errors.issuer
            ? { formHelperText: { sx: { color: 'warning.main' } } }
            : undefined
        }
      />

      <TextField
        label="Audience"
        value={value.audience}
        onChange={(e) => onChange('audience', e.target.value)}
        error={!!errors.audience}
        helperText={
          errors.audience ||
          'Which of your services these logins are for. Required — it stops a sign-in meant for another of your services being used here.'
        }
        placeholder="https://api.acme.com"
        size="small"
        fullWidth
        required
        disabled={disabled}
      />

      <TextField
        label="Keys URL (JWKS)"
        value={value.jwks_uri}
        onChange={(e) => onChange('jwks_uri', e.target.value)}
        error={!!errors.jwks_uri}
        helperText={errors.jwks_uri || 'Usually your issuer + /.well-known/jwks.json'}
        placeholder="https://acme.auth0.com/.well-known/jwks.json"
        size="small"
        fullWidth
        required
        disabled={disabled}
      />

      <TextField
        label="Required scopes (optional)"
        value={value.required_scopes}
        onChange={(e) => onChange('required_scopes', e.target.value)}
        helperText="Space or comma separated. A sign-in missing any of these is refused."
        placeholder="read:orders write:orders"
        size="small"
        fullWidth
        disabled={disabled}
      />

      <Box>
        <Button
          size="small"
          onClick={() => setShowAdvanced((v) => !v)}
          startIcon={showAdvanced ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          sx={{ textTransform: 'none', color: 'text.secondary' }}
        >
          Advanced
        </Button>
        <Collapse in={showAdvanced}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1.5 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.45 }}>
              Only needed if your provider issues opaque tokens — ones with no keys URL to check
              them against. Most don’t; leave these blank if you’re unsure.
            </Typography>
            <TextField
              label="Introspection endpoint"
              value={value.introspection_endpoint}
              onChange={(e) => onChange('introspection_endpoint', e.target.value)}
              error={!!errors.introspection_endpoint}
              helperText={
                errors.introspection_endpoint ||
                'Used instead of the keys URL to check each token with your provider.'
              }
              placeholder="https://acme.auth0.com/oauth/introspect"
              size="small"
              fullWidth
              disabled={disabled}
            />
            <TextField
              label="Client ID"
              value={value.client_id}
              onChange={(e) => onChange('client_id', e.target.value)}
              size="small"
              fullWidth
              disabled={disabled}
            />
            <TextField
              label="Client secret"
              value={value.client_secret}
              onChange={(e) => onChange('client_secret', e.target.value)}
              helperText={
                secretIsStored
                  ? 'Leave the masked value alone to keep the stored secret.'
                  : 'Only used to call the introspection endpoint.'
              }
              size="small"
              fullWidth
              disabled={disabled}
            />
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

export default McpOAuthFields;
